import React from 'react';
import { useTranslation } from 'react-i18next';
import GridContainer from '@/components/Grid/GridContainer';
import CustomInput from '@/components/CustomInput/CustomInput';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import GridItem from '@/components/Grid/GridItem';
import LineItemsColumnList from './LineItemsColumnList';

export default function ReportingColumnList({
  columns,
  onUpdateColumn,
  onRemoveColumn,
  onAddColumn,
  availableColumns,
  isDisabled,
  columnTypeLabel = 'Column',
  availableLineItemColumns = [
    {
      value: 'gl_code_1',
      name: 'GL Code 1',
    },
    {
      value: 'gl_code_2',
      name: 'GL Code 2',
    },
  ],
}) {
  const { t } = useTranslation();
  return (
    <>
      {columns?.map((column, index) => (
        <GridContainer key={index}>
          <GridItem xs={12} sm={12} md={4}>
            <CustomInput
              type='text'
              labelText={t('reportingColumnList.columnName', {
                label: columnTypeLabel,
              })}
              onChange={(e) =>
                onUpdateColumn(index, 'column_name', e.target.value)
              }
              value={column.column_name}
              disabled={isDisabled}
              formControlProps={{
                fullWidth: true,
              }}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={4}>
            <div className='custom-select'>
              <FormControl fullWidth variant='standard'>
                <InputLabel id={`available-${columnTypeLabel}-label-${index}`}>
                  {t('reportingColumnList.availableColumns')}
                </InputLabel>
                <Select
                  labelId={`available-${columnTypeLabel}-label-${index}`}
                  id={`available-${columnTypeLabel}-${index}`}
                  value={column.variable_name}
                  onChange={(e) =>
                    onUpdateColumn(index, 'variable_name', e.target.value)
                  }
                  disabled={isDisabled}>
                  {availableColumns?.map((col) => (
                    <MenuItem key={col.value} value={col.value}>
                      {col.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </GridItem>
          {column.variable_name === 'custom' && (
            <GridItem xs={12} sm={12} md={3}>
              <CustomInput
                type='text'
                labelText={t('reportingColumnList.customHardcodedValue')}
                onChange={(e) =>
                  onUpdateColumn(index, 'column_custom_value', e.target.value)
                }
                value={column.column_custom_value}
                name='column_custom_value'
                formControlProps={{
                  fullWidth: true,
                }}
                disabled={isDisabled}
              />
            </GridItem>
          )}
          {column.variable_name !== 'custom' && (
            <GridItem xs={12} sm={12} md={3}>
              <div className='custom-select'>
                {column.variable_name &&
                  availableColumns.find(
                    (col) => col.value === column.variable_name,
                  )?.formats && (
                    <FormControl variant='standard' fullWidth>
                      <InputLabel id={`format-label-${index}`}>
                        {t('reportingColumnList.format')}
                      </InputLabel>
                      <Select
                        labelId={`format-label-${index}`}
                        id={`format-${index}`}
                        name='format'
                        value={column.format}
                        onChange={(e) =>
                          onUpdateColumn(index, 'format', e.target.value)
                        }
                        disabled={isDisabled}>
                        {availableColumns
                          .find((col) => col.value === column.variable_name)
                          ?.formats?.map((format) => (
                            <MenuItem key={format.value} value={format.value}>
                              {format.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  )}
              </div>
            </GridItem>
          )}
          <GridItem
            xs='auto'
            md={1}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
            }}>
            <Tooltip
              title={t('reportingColumnList.removeColumn', {
                label: columnTypeLabel,
              })}>
              <span>
                <IconButton
                  size='small'
                  onClick={() => onRemoveColumn(index)}
                  disabled={isDisabled}>
                  <DeleteOutlineIcon
                    fontSize='small'
                    style={{ color: '#f44336' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </GridItem>
          {column.variable_name === 'line_items' && (
            <LineItemsColumnList
              column={column}
              index={index}
              onUpdateColumn={onUpdateColumn}
              isDisabled={isDisabled}
              availableLineItemColumns={availableLineItemColumns}
            />
          )}
        </GridContainer>
      ))}

      <div>
        <Tooltip
          title={t('reportingColumnList.addColumn', {
            label: columnTypeLabel,
          })}>
          <span>
            <IconButton
              size='small'
              onClick={onAddColumn}
              disabled={isDisabled}
              style={{ marginTop: '6px', color: '#9e9e9e' }}>
              <AddCircleOutlineIcon fontSize='small' />
            </IconButton>
          </span>
        </Tooltip>
      </div>
    </>
  );
}
